/**
 * Distance calculator utility for CabZigo
 * This module handles distance calculations between cities and pricing based on distance
 * using Google Maps Distance Matrix API through the backend
 */

// Import Google Maps API utility
import { getDrivingDistanceAndDuration } from './googleMapsApi';

/**
 * Get distance between two cities using Google Maps Distance Matrix API via backend
 * @param {string} city1 - First city name
 * @param {string} city2 - Second city name
 * @returns {Promise<number>} - Distance in kilometers
 */
const getDistanceBetweenCities = async (city1, city2) => {
  if (!city1 || !city2) {
    console.error('Both origin and destination cities are required');
    return 0;
  }

  try {
    const result = await getDrivingDistanceAndDuration(city1, city2);
    return result.distance || 0;
  } catch (error) {
    console.error('Error calculating distance:', error);
    return 0; // Return 0 as fallback
  }
};

/**
 * Get estimated travel time between two cities
 * @param {string} city1 - First city name
 * @param {string} city2 - Second city name
 * @returns {Promise<number>} - Duration in hours
 */
const getEstimatedTime = async (city1, city2) => {
  if (!city1 || !city2) {
    console.error('Both origin and destination cities are required');
    return 0;
  }

  try {
    const result = await getDrivingDistanceAndDuration(city1, city2);
    return result.duration || 0;
  } catch (error) {
    console.error('Error calculating travel time:', error);
    return 0; // Return 0 as fallback
  }
};

/**
 * Calculate cab prices based on distance and cab type
 * @param {number} distance - Distance in kilometers
 * @param {object} cabModel - Cab model details
 * @param {string} journeyType - 'oneWay' or 'roundTrip'
 * @returns {object} - Object containing calculated prices
 */
const calculatePricesByDistance = (distance, cabModel, journeyType = 'oneWay') => {
  if (!cabModel || !cabModel.baseFare || !cabModel.perKmRate) {
    console.error('Invalid cab model provided');
    return {
      baseFare: 0,
      totalFare: 0,
      perKmRate: 0,
      distance: distance || 0,
      journeyType
    };
  }

  try {
    // Calculate base fare (minimum charge)
    const baseFare = parseFloat(cabModel.baseFare) || 0;
    
    // Get per km rate
    const perKmRate = parseFloat(cabModel.perKmRate) || 0;
    
    // Calculate distance fare (minimum 1 km)
    const effectiveDistance = Math.max(1, distance || 0);
    const distanceFare = effectiveDistance * perKmRate;
    
    // Calculate total fare
    let totalFare = baseFare + distanceFare;
    
    // Apply round trip multiplier if needed
    if (journeyType === 'roundTrip') {
      totalFare = totalFare * 1.8; // 10% discount on return trip (2 * 0.9 = 1.8)
    }
    
    // Add GST (18%)
    const gstRate = 0.18;
    const gstAmount = totalFare * gstRate;
    totalFare += gstAmount;
    
    // Add toll and state tax (fixed amounts for simplicity)
    const tollTaxAmount = Math.min(500, Math.max(50, effectiveDistance * 1.5)); // Between 50 and 500
    const stateTaxAmount = effectiveDistance * 0.5; // 0.5 per km
    
    // Calculate all-inclusive price
    const allInclusivePrice = totalFare + tollTaxAmount + stateTaxAmount;
    
    // Calculate included kilometers
    const includedKm = journeyType === 'roundTrip' ? distance * 2 : distance;
    
    return {
      baseFare,
      totalFare: Math.round(totalFare),
      allInclusivePrice: Math.round(allInclusivePrice),
      perKmRate,
      distance: effectiveDistance,
      journeyType,
      includedKm,
      taxes: {
        gst: {
          rate: gstRate * 100,
          amount: Math.round(gstAmount)
        },
        toll: {
          amount: Math.round(tollTaxAmount)
        },
        state: {
          amount: Math.round(stateTaxAmount)
        }
      }
    };
  } catch (error) {
    console.error('Error calculating prices:', error);
    return {
      baseFare: 0,
      totalFare: 0,
      perKmRate: 0,
      distance: distance || 0,
      journeyType,
      error: 'Failed to calculate fare'
    };
  }
};

// Export the functions
export {
  getDistanceBetweenCities,
  getEstimatedTime,
  calculatePricesByDistance
};
